import { Code, CopyButton, Button, Group, Text } from '@repletoryreach/sdk-ui';
import { useT } from '../../../i18n/I18nProvider';

/**
 * A block of code with a copy button, used everywhere on this page.
 *
 * `Code` and `CopyButton` are both already in the bundle and already used on
 * the API keys page; nothing here needs a syntax highlighter, and adding one
 * would pull in a dependency the console does not carry for the sake of
 * colouring a curl command.
 */
export function CodeBlock({
  code,
  label,
  caption
}: {
  code: string;
  /** Names what the block is, for the copy button's accessible label. */
  label?: string;
  /** One line under the block, where the block needs explaining. */
  caption?: string;
}) {
  const t = useT();
  return (
    <div className="rr-dev__code">
      <div className="rr-dev__code-bar">
        {label && (
          <Text role="caption" c="var(--rr-color-text-muted)">
            {label}
          </Text>
        )}
        <CopyButton value={code}>
          {({ copied, failed, copy }) => (
            <Button
              size="xs"
              variant="subtle"
              onClick={copy}
              aria-label={label ? t('developers.copyNamed', { name: label }) : t('action.copy')}
            >
              {failed ? t('common.copyFailed') : copied ? t('common.copied') : t('action.copy')}
            </Button>
          )}
        </CopyButton>
      </div>
      <Code block className="rr-dev__pre">
        {code}
      </Code>
      {caption && (
        <Text role="caption" c="var(--rr-color-text-muted)">
          {caption}
        </Text>
      )}
    </div>
  );
}

/** The coloured pill a method gets, so a reader can scan the list by verb. */
export function MethodBadge({ method }: { method: string }) {
  const colour =
    method === 'GET' ? 'info' : method === 'DELETE' ? 'error' : method === 'POST' ? 'success' : 'warning';
  return (
    <span className={`rr-dev__method rr-dev__method--${method.toLowerCase()}`} data-colour={colour}>
      {method}
    </span>
  );
}
